import type {
	Event,
	NotifiableError,
	OnErrorCallback,
} from '@bugsnag/js';

export type ErrorSeverity = 'info' | 'warning' | 'error';

type SynchronousOnErrorCallback = (event: Event) => ReturnType<OnErrorCallback>;

const ERROR_REPORTED = Symbol.for('creator.bugsnag.errorReported');

/**
 * Check whether an error has already been handed to Bugsnag explicitly.
 *
 * This lets a boundary that intentionally catches an error avoid reporting it
 * a second time while still reporting unrelated failures from the same block.
 *
 * @param error Value that may have been reported previously.
 * @returns Whether {@link markErrorReported} marked the value.
 */
export function isErrorReported(error: unknown): boolean {
	if (
		!error
		|| typeof error !== 'object'
	) {
		return false;
	}

	try {
		return Boolean((error as Record<symbol, true | undefined>)[ERROR_REPORTED]);
	} catch {
		return false;
	}
}

/**
 * Mark an object error as explicitly reported without adding enumerable data.
 *
 * @param error Error-like value to mark.
 * @returns The original value, marked in place when it is an object.
 */
export function markErrorReported<E>(error: E): E {
	if (
		error
		&& typeof error === 'object'
	) {
		try {
			Object.defineProperty(
				error,
				ERROR_REPORTED,
				{
					configurable: true,
					value: true,
				},
			);
		} catch {
			/**
			 * Reporting state is best-effort and must never mask the error.
			 */
		}
	}

	return error;
}

/**
 * Report an error to Bugsnag when the client is available.
 *
 * Replaces the repeated `if (typeof window.glBugsnagClient !== 'undefined')`
 * guard + `notify(...)` boilerplate scattered across the codebase. The client
 * is only present once `configureBugsnag` has run, so callers that may fire
 * before/around launch must not assume it exists — this no-ops when it does
 * not.
 *
 * @param error Error (or notifiable value) to report.
 * @param severity Bugsnag severity to stamp on the event. Defaults to `warning`.
 * @param onError Optional callback for extra event shaping (e.g. `addMetadata`).
 *   Runs after the severity is applied. Return values, promises and two-argument
 *   completion callbacks retain Bugsnag's corresponding completion contract.
 * @returns Whether the error was handed to an initialized Bugsnag client.
 */
export default function reportError(
	error: unknown,
	severity: ErrorSeverity = 'warning',
	onError?: OnErrorCallback,
): boolean {
	/*
	 * Guard `window` itself first: `typeof window.glBugsnagClient` throws a
	 * ReferenceError in non-browser contexts (node scripts, SSR, node-env tests)
	 * where `window` is undefined.
	 */
	if (
		typeof window === 'undefined'
		|| typeof window.glBugsnagClient === 'undefined'
	) {
		return false;
	}

	try {
		/* Bugsnag treats exactly two formal parameters as callback-based async. */
		const callback: OnErrorCallback = onError?.length === 2
			? (event, done) => {
				let completed = false;
				const complete: Parameters<OnErrorCallback>[1] = (callbackError, shouldSend) => {
					if (!completed) {
						completed = true;
						done(
							callbackError,
							shouldSend,
						);
					}
				};

				try {
					event.severity = severity;
					return onError(
						event,
						complete,
					);
				} catch {
					complete(
						null,
						false,
					);
					return undefined;
				}
			}
			: (event) => {
				try {
					event.severity = severity;
					return (onError as SynchronousOnErrorCallback | undefined)?.(event);
				} catch {
					/** A telemetry callback must not escape into application flow. */
					return false;
				}
			};

		window.glBugsnagClient.notify(
			error as NotifiableError,
			callback,
		);

		return true;
	} catch {
		/**
		 * Telemetry must never interrupt the application error path.
		 */
		return false;
	}
}
